import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import { useModuleRetake } from "@/hooks/useModuleRetake";
import RetakeModuleDialog from "@/components/modules/RetakeModuleDialog";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import { useAccounts, type Account } from "@/hooks/useAccounts";
import ModuleShell from "@/components/lunimar/LunimarModuleShell";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, ShieldAlert, ShieldCheck, AlertTriangle, CheckCircle2,
  Mail, MessageSquare, RotateCcw, DollarSign, Fingerprint, Smile,
  Inbox, ShoppingBag, Globe, Smartphone, FileText, AtSign, ChevronRight,
  Search, Eye, Loader2, XCircle,
} from "lucide-react";
import {
  startModuleAttempt, commitModuleAttempt, trackModule,
} from "@/lib/learning/telemetry";
import { SCAM_EVENTS, MODULE_CONCEPT_TAGS } from "@/lib/learning/moduleEvents";
import {
  SCAM_SCENARIOS, pickScenarioMix, FROZEN_DICTIONARY_CONCEPTS,
  type ScamScenario, type Hotspot, type ActionId, type AccountTarget,
} from "@/data/scamScenarios";
import { applyScamConsequence } from "@/lib/learning/scamConsequences";
import { useSfx } from "@/hooks/useSfx";
import { useSceneMusic } from "@/hooks/useSceneMusic";
import { useInstructorVoice, useTriggerInstructorVoice, usePreloadInstructorVoice } from "@/hooks/useInstructorVoice";
import { useAccessTier } from "@/hooks/useAccessTier";
import { useLaunchModuleAccess } from "@/components/launch/LaunchModuleGate";

const SCAM_VOICE_PRELOAD: string[] = [
  "scam.intro",
  "scam.preFirstMessage",
  "scam.hint.suspiciousLink",
  "scam.warning.fakeAuthority",
  "scam.warning.urgency",
  "scam.warning.tooGood",
  "scam.partial.blockedButUnclear",
  "scam.success.redFlagIdentified",
  "scam.postDecision.fail",
  "scam.postDecision.win",
];
import type { MusicKey } from "@/lib/audio/musicMap";

/**
 * Scam Defense Simulator — immersive rebuild.
 *
 *  - Uses the learner's EXISTING HeirWise accounts (no fake balances).
 *  - Requires the learner to identify evidence (hotspots) before acting.
 *  - "Block & Report" is no longer the universally-correct action.
 *  - Consequences write traceable rows to account_transactions / accounts
 *    via applyScamConsequence — only at end-of-run, idempotent per run_id.
 *  - All existing telemetry preserved + extended (centralized via trackModule).
 *  - Module reward fires exactly once. Replay spawns a fresh run_id.
 *  - QA accounts already skip rollups in telemetry.ts.
 */

type Stage = "intro" | "onboard_accounts" | "scenario" | "consequence" | "outcome";

interface Resources { identityRisk: number; composure: number }
const START_RESOURCES: Resources = { identityRisk: 0, composure: 75 };
const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

interface DecisionRecord {
  scenarioId: string;
  actionId: ActionId;
  isBest: boolean;
  hotspotsFound: number;
  hotspotsTotal: number;
  falsePositives: number;
  moneyDelta: number;
  account?: AccountTarget;
  identityRiskDelta: number;
  composureDelta: number;
  message: string;
  triggersRecovery?: boolean;
}

const SOURCE_ICON: Record<string, React.ElementType> = {
  Inbox: Inbox,
  Texts: Smartphone,
  Marketplace: ShoppingBag,
  "Social DMs": AtSign,
  Browser: Globe,
};

const TYPE_ICON = {
  email: Mail, text: MessageSquare, marketplace: ShoppingBag,
  social_dm: AtSign, payment_app: DollarSign, browser: Globe,
  phone: Smartphone, qr: FileText, invoice: FileText,
} as const;

/** Legacy dataLayer push retained alongside telemetry ledger. */
function track(event: string, payload: Record<string, unknown>) {
  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    if (w.dataLayer && Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event, ...payload });
    }
  } catch { /* no-op */ }
}

const ScamDefenseModule = () => {
  const navigate = useNavigate();
  const { tier: accessTier } = useAccessTier();
  const betaModuleUnlocked = useLaunchModuleAccess("scam_defense");
  const isBetaModuleExperience = accessTier !== "admin" || betaModuleUnlocked;
  const backHref = isBetaModuleExperience ? "/dashboard" : "/curriculum";
  const backLabel = "Back to Dashboard";
  const { moduleGates, loading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();
  const { reverseAndReset, pending: scamRetakePending } = useModuleRetake();
  const [scamRetakeOpen, setScamRetakeOpen] = useState(false);
  const handleScamRetake = async () => {
    const r = await reverseAndReset("scam_defense");
    if (r) { setScamRetakeOpen(false); window.location.reload(); }
  };
  const { accounts, loading: accountsLoading, getAccount, refetch } = useAccounts();
  const { playSfx } = useSfx();
  const { playMoment: playVoiceMoment } = useInstructorVoice();

  const [runId, setRunId] = useState<string>(() => crypto.randomUUID());
  const [queue, setQueue] = useState<ScamScenario[]>(() => pickScenarioMix(4));
  const [idx, setIdx] = useState(0);
  const [stage, setStage] = useState<Stage>("intro");
  const sceneKey: MusicKey = stage === "intro"
    ? "moduleMissionStart"
    : stage === "outcome"
      ? "moduleCompletion"
      : stage === "consequence"
        ? "reflectionState"
        : "scamSignal";
  useSceneMusic(sceneKey);
  // Voice: module intro fires on the first paint of the intro stage.
  useTriggerInstructorVoice("scam.intro", stage === "intro");
  // Beta v2: on first scenario only, Lumi reminds the learner to slow
  // down before reading. Fires once per mount per momentId.
  useTriggerInstructorVoice(
    "scam.preFirstMessage",
    stage === "scenario" && idx === 0,
  );
  usePreloadInstructorVoice(SCAM_VOICE_PRELOAD);
  const [foundHotspots, setFoundHotspots] = useState<Record<string, Set<string>>>({});
  const [falsePositives, setFalsePositives] = useState<Record<string, Set<string>>>({});
  const [submitted, setSubmitted] = useState<Record<string, boolean>>({});
  const [resources, setResources] = useState<Resources>(START_RESOURCES);
  const [decisions, setDecisions] = useState<DecisionRecord[]>([]);
  const [pendingDecision, setPendingDecision] = useState<DecisionRecord | null>(null);
  const [committing, setCommitting] = useState(false);
  const rewardFiredRef = useRef(false);

  const gate = moduleGates.find(g => g.moduleKey === "scam_defense");
  // Beta users are pre-validated by LaunchModuleGate; skip curriculum gating.
  const isLocked = isBetaModuleExperience ? false : !gate?.isUnlocked;

  const currentScenario = queue[idx];
  const totalScenarios = queue.length;
  const totalSteps = 2 + totalScenarios;

  const hasAccounts = accounts.length > 0;

  /** Active (live) balances for the meters. We display real balances at all times;
   * deltas are journaled into decisions[] and committed at run end. */
  const checking = getAccount("checking");
  const savings = getAccount("savings");

  /** Forecasted balance reflects pending sim losses already chosen in this run. */
  const forecast = useMemo(() => {
    const base: Record<AccountTarget, number> = {
      checking: checking?.balance ?? 0,
      savings: savings?.balance ?? 0,
      spending: checking?.balance ?? 0, // map "spending" to checking when no dedicated spending account
    };
    for (const d of decisions) {
      if (d.moneyDelta < 0 && d.account) {
        base[d.account] = Math.max(0, base[d.account] + d.moneyDelta);
      }
    }
    return base;
  }, [decisions, checking, savings]);

  /* ------------------------- Stage handlers ------------------------- */

  const handleStart = () => {
    if (!hasAccounts) { setStage("onboard_accounts"); return; }
    playSfx("module_start");
    track("simulator_start", { module_key: "scam_defense", run_id: runId });
    void startModuleAttempt({
      moduleSlug: "scam_defense", attemptId: runId,
      conceptTags: MODULE_CONCEPT_TAGS.scam_defense,
      metadata: { event_name: "scam_run_started" },
    });
    void trackModule({
      eventType: SCAM_EVENTS.SCENARIO_STARTED, moduleSlug: "scam_defense", attemptId: runId,
      sourceId: `scam:${runId}:${currentScenario.id}:start`,
      metadata: { scenario_id: currentScenario.id, scam_type: currentScenario.type, event_name: "scam_scenario_started" },
    });
    setStage("scenario");
  };

  const handleHotspotClick = (h: Hotspot) => {
    const key = currentScenario.id;
    if (submitted[key]) return;
    setFoundHotspots(prev => {
      const existing = prev[key] ?? new Set<string>();
      if (existing.has(h.id)) return prev;
      const next = new Set(existing); next.add(h.id);
      return { ...prev, [key]: next };
    });
    // Beta v2: concept-tagged hints. The controller dedupes by momentId
    // so each line plays at most once across the whole run, regardless
    // of how many scenarios surface the same red-flag concept.
    if (h.concept === "verification_habit") {
      playVoiceMoment("scam.hint.suspiciousLink");
    } else if (h.concept === "authority_impersonation") {
      playVoiceMoment("scam.warning.fakeAuthority");
    } else if (h.concept === "urgency_manipulation") {
      playVoiceMoment("scam.warning.urgency");
    } else if (h.concept === "too_good_to_be_true") {
      playVoiceMoment("scam.warning.tooGood");
    }
    void trackModule({
      eventType: SCAM_EVENTS.RED_FLAG_OK, moduleSlug: "scam_defense", attemptId: runId,
      sourceId: `scam:${runId}:${currentScenario.id}:hotspot:${h.id}`,
      correctness: true,
      conceptTag: h.concept ?? null,
      metadata: {
        event_name: "scam_hotspot_identified",
        scenario_id: currentScenario.id, hotspot_id: h.id, severity: h.severity,
      },
    });
  };

  const handleFlagWord = (tokenId: string, word: string) => {
    const key = currentScenario.id;
    if (submitted[key]) return;
    playSfx("click_secondary");
    setFalsePositives(prev => {
      const existing = prev[key] ?? new Set<string>();
      const next = new Set(existing);
      if (next.has(tokenId)) next.delete(tokenId);
      else next.add(tokenId);
      return { ...prev, [key]: next };
    });
  };

  const handleSubmitEvidence = () => {
    const key = currentScenario.id;
    if (submitted[key]) return;
    playSfx("decision_lock");
    const found = foundHotspots[key]?.size ?? 0;
    const fps = falsePositives[key]?.size ?? 0;
    setSubmitted(prev => ({ ...prev, [key]: true }));
    void trackModule({
      eventType: "scam_evidence_submitted", moduleSlug: "scam_defense", attemptId: runId,
      sourceId: `scam:${runId}:${key}:evidence`,
      metadata: {
        event_name: "scam_evidence_submitted",
        scenario_id: key,
        hotspots_found: found,
        hotspots_total: currentScenario.hotspots.length,
        false_positives: fps,
      },
    });
  };

  const handleAction = (actionId: ActionId) => {
    const sc = currentScenario;
    const c = sc.consequences[actionId] ?? {
      moneyDelta: 0, identityRiskDelta: 0, composureDelta: 0, message: "No effect.",
    };
    const found = foundHotspots[sc.id]?.size ?? 0;
    const missed = sc.hotspots.length - found;
    const fps = falsePositives[sc.id]?.size ?? 0;
    const isBest = actionId === sc.bestAction;
    const foundRatio = sc.hotspots.length > 0 ? found / sc.hotspots.length : 0;
    if (isBest) {
      playSfx("correct_soft");
      // Voice: if the learner actually surfaced evidence first, name the
      // skill directly; otherwise fall back to the generic win line.
      // Only one fires — the controller stops any prior line.
      playVoiceMoment(found >= 1 ? "scam.success.redFlagIdentified" : "scam.postDecision.win");
    } else if ((c.moneyDelta ?? 0) < 0) {
      // Risky/scam action with real financial loss
      playSfx("money_loss");
      // Voice: name urgency as the mechanism, without shaming the learner.
      playVoiceMoment("scam.postDecision.fail");
    } else {
      // Risky but non-financial wrong action (clicked link, trusted request)
      playSfx("warning_risk");
      // Voice: if the learner blocked/reported without surfacing the
      // red flags, coach the pattern instead of repeating the fail line.
      if ((actionId === "report_phishing" || actionId === "block_sender") && foundRatio < 0.5) {
        playVoiceMoment("scam.partial.blockedButUnclear");
      } else {
        playVoiceMoment("scam.postDecision.fail");
      }
    }

    const record: DecisionRecord = {
      scenarioId: sc.id, actionId, isBest,
      hotspotsFound: found, hotspotsTotal: sc.hotspots.length,
      falsePositives: fps,
      moneyDelta: c.moneyDelta, account: c.account,
      identityRiskDelta: c.identityRiskDelta, composureDelta: c.composureDelta,
      message: c.message, triggersRecovery: c.triggersRecovery,
    };

    setDecisions(d => [...d, record]);
    setResources(r => ({
      identityRisk: clamp(r.identityRisk + c.identityRiskDelta, 0, 100),
      composure: clamp(r.composure + c.composureDelta, 0, 100),
    }));
    setPendingDecision(record);
    // Delayed panel reveal — sequel to the primary action sound.
    setTimeout(() => playSfx("panel_open"), 320);

    // Telemetry
    const tone = sc.actions.find(a => a.id === actionId)?.tone ?? "neutral";
    track("simulator_decision", { module_key: "scam_defense", run_id: runId, scenario_id: sc.id, action_id: actionId, best: isBest });
    void trackModule({
      eventType: SCAM_EVENTS.CHOICE, moduleSlug: "scam_defense", attemptId: runId,
      sourceId: `scam:${runId}:${sc.id}:${actionId}`,
      correctness: isBest,
      metadata: {
        event_name: "scam_action_selected",
        scenario_id: sc.id, action_id: actionId, tone, is_best: isBest,
        hotspots_found: found, hotspots_total: sc.hotspots.length,
      },
    });
    if (missed > 0) {
      void trackModule({
        eventType: SCAM_EVENTS.RED_FLAG_MISSED, moduleSlug: "scam_defense", attemptId: runId,
        sourceId: `scam:${runId}:${sc.id}:missed`,
        metadata: { event_name: "scam_hotspot_missed", scenario_id: sc.id, red_flags_missed: missed },
      });
    }
    if (c.moneyDelta < 0) {
      void trackModule({
        eventType: SCAM_EVENTS.MONEY_LOST, moduleSlug: "scam_defense", attemptId: runId,
        sourceId: `scam:${runId}:${sc.id}:loss`,
        scoreDelta: c.moneyDelta,
        metadata: { event_name: "scam_money_lost", scenario_id: sc.id, amount: Math.abs(c.moneyDelta), account: c.account, account_effect_status: "draft" },
      });
    } else if (isBest) {
      void trackModule({
        eventType: SCAM_EVENTS.MONEY_SAVED, moduleSlug: "scam_defense", attemptId: runId,
        sourceId: `scam:${runId}:${sc.id}:save`,
        metadata: { event_name: "scam_money_protected", scenario_id: sc.id },
      });
    }
    if (c.identityRiskDelta > 0) {
      void trackModule({
        eventType: "scam_identity_risk_increased", moduleSlug: "scam_defense", attemptId: runId,
        sourceId: `scam:${runId}:${sc.id}:idrisk`,
        metadata: { event_name: "scam_identity_risk_increased", delta: c.identityRiskDelta },
      });
    }
    if (c.triggersRecovery) {
      void trackModule({
        eventType: "scam_recovery_started", moduleSlug: "scam_defense", attemptId: runId,
        sourceId: `scam:${runId}:${sc.id}:recovery`,
        metadata: { event_name: "scam_recovery_started", scenario_id: sc.id },
      });
    }
    setStage("consequence");
  };

  const handleAdvance = async () => {
    playSfx("panel_close");
    setPendingDecision(null);
    if (idx + 1 < totalScenarios) {
      setIdx(idx + 1);
      setStage("scenario");
      void trackModule({
        eventType: SCAM_EVENTS.SCENARIO_STARTED, moduleSlug: "scam_defense", attemptId: runId,
        sourceId: `scam:${runId}:${queue[idx + 1].id}:start`,
        metadata: { event_name: "scam_scenario_started", scenario_id: queue[idx + 1].id },
      });
      return;
    }
    // End of run — commit consequences.
    if (rewardFiredRef.current) { setStage("outcome"); return; }
    rewardFiredRef.current = true;
    setCommitting(true);
    await commitConsequences();
    completeModule("scam_defense");
    void commitModuleAttempt({
      moduleSlug: "scam_defense", attemptId: runId,
      metadata: {
        event_name: "scam_run_completed",
        decisions: decisions.length,
        scams_defeated: decisions.filter(d => d.isBest).length,
        money_lost: decisions.reduce((s, d) => s + (d.moneyDelta < 0 ? -d.moneyDelta : 0), 0),
        identity_risk: resources.identityRisk,
      },
    });
    await refetch();
    setCommitting(false);
    setStage("outcome");
    playSfx("module_complete");
  };

  const commitConsequences = async () => {
    for (const d of decisions) {
      if (d.moneyDelta < 0 && d.account) {
        const target =
          d.account === "savings"
            ? getAccount("savings")
            : getAccount("checking"); // spending mapped to checking when no dedicated account
        if (!target) continue;
        await applyScamConsequence({
          runId, scenarioId: d.scenarioId, account: target,
          amount: Math.abs(d.moneyDelta),
          description: `Scam Defense — ${d.message.slice(0, 80)}`,
          kind: "simulated_scam_loss",
        });
      } else if (d.isBest) {
        // Journal the avoided loss when there was money on the line in any path.
        const sc = SCAM_SCENARIOS.find(s => s.id === d.scenarioId);
        const worst = sc
          ? Object.values(sc.consequences).reduce((m, c) => Math.min(m, c?.moneyDelta ?? 0), 0)
          : 0;
        if (worst < 0) {
          const target = getAccount("checking");
          if (target) {
            await applyScamConsequence({
              runId, scenarioId: d.scenarioId, account: target,
              amount: Math.abs(worst),
              description: `Scam Defense — avoided loss`,
              kind: "simulated_scam_loss_avoided",
            });
          }
        }
      }
    }
  };

  const handleReplay = () => {
    const newRunId = crypto.randomUUID();
    track("simulator_replay", { module_key: "scam_defense", prev_run_id: runId, new_run_id: newRunId });
    void trackModule({
      eventType: "scam_replay_started", moduleSlug: "scam_defense", attemptId: newRunId,
      sourceId: `scam:${newRunId}:replay`,
      metadata: { event_name: "scam_replay_started", prev_run_id: runId },
    });
    rewardFiredRef.current = false;
    setRunId(newRunId);
    setQueue(pickScenarioMix(4));
    setIdx(0);
    setStage("scenario");
    setFoundHotspots({});
    setFalsePositives({});
    setSubmitted({});
    setResources(START_RESOURCES);
    setDecisions([]);
    setPendingDecision(null);
    void startModuleAttempt({
      moduleSlug: "scam_defense", attemptId: newRunId,
      conceptTags: MODULE_CONCEPT_TAGS.scam_defense,
      metadata: { event_name: "scam_run_started" },
    });
  };

  /* ------------------------- Render ------------------------- */

  const stageIndex = stage === "intro" || stage === "onboard_accounts" ? 0
    : stage === "outcome" ? totalSteps - 1
    : 1 + idx;
  const stepKey = `${runId}:${stage}:${idx}`;

  const conceptsPracticed = useMemo(() => {
    const keys = new Set<string>();
    for (const d of decisions) {
      const sc = SCAM_SCENARIOS.find(s => s.id === d.scenarioId);
      sc?.conceptKeys.forEach(k => { if (FROZEN_DICTIONARY_CONCEPTS[k]) keys.add(k); });
    }
    return Array.from(keys);
  }, [decisions]);

  if (loading || accountsLoading) return null;

  return (
    <ModuleShell
      realmKey="scam_defense"
      title="Scam Defense"
      stepIndex={stageIndex}
      totalSteps={totalSteps}
      isLocked={isLocked}
      lockMessage={gate?.missingPrereqs?.length ? `Complete: ${gate.missingPrereqs.join(", ")}` : "Complete Financial Scams & Fraud Awareness to unlock."}
      isCompleted={false}
      stepKey={stepKey}
    >
      {stage === "intro" && (
        <IntroBriefing
          totalScenarios={totalScenarios}
          hasAccounts={hasAccounts}
          accounts={accounts}
          onStart={handleStart}
        />
      )}

      {stage === "onboard_accounts" && (
        <OnboardAccounts onSetup={() => navigate("/finances")} />
      )}

      {(stage === "scenario" || stage === "consequence") && currentScenario && (
        <SimulatorView
          scenario={currentScenario}
          index={idx}
          total={totalScenarios}
          forecast={forecast}
          resources={resources}
          foundHotspots={foundHotspots[currentScenario.id] ?? new Set()}
          falsePositives={falsePositives[currentScenario.id] ?? new Set()}
          submitted={!!submitted[currentScenario.id]}
          onHotspot={handleHotspotClick}
          onFlagWord={handleFlagWord}
          onSubmitEvidence={handleSubmitEvidence}
          onAction={handleAction}
          decisions={decisions}
          pendingDecision={pendingDecision}
          onAdvance={handleAdvance}
          stage={stage}
        />
      )}

      {stage === "outcome" && (
        <OutcomeScreen
          decisions={decisions}
          resources={resources}
          forecast={forecast}
          conceptKeys={conceptsPracticed}
          committing={committing}
          accounts={accounts}
          onReplay={handleReplay}
          onExit={() => navigate(backHref)}
          exitLabel={backLabel}
          onRetakeModule={() => setScamRetakeOpen(true)}
        />
      )}
      <RetakeModuleDialog
        open={scamRetakeOpen}
        onOpenChange={setScamRetakeOpen}
        moduleDisplayName="Scam Defense"
        onConfirm={handleScamRetake}
        pending={scamRetakePending}
      />
    </ModuleShell>
  );
};

/* ============================ Subcomponents ============================ */

const money = (n: number) => `$${n.toFixed(2)}`;

const IntroBriefing = ({
  totalScenarios, hasAccounts, accounts, onStart,
}: { totalScenarios: number; hasAccounts: boolean; accounts: Account[]; onStart: () => void }) => {
  const checking = accounts.find(a => a.account_type === "checking");
  const savings = accounts.find(a => a.account_type === "savings");
  return (
    <div className="space-y-5">
      <div className="flex flex-col items-center text-center space-y-3">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20">
          <ShieldAlert className="w-8 h-8 text-primary" />
        </div>
        <h2 className="font-heading text-xl font-bold text-foreground">Scam Defense Simulator</h2>
        <p className="text-sm text-muted-foreground max-w-md">
          You're managing your digital life today. Inspect each message, identify the red flags,
          and choose carefully. This is <span className="text-foreground font-medium">your HeirWise money</span> —
          your choices can protect it, or cost it.
        </p>
      </div>

      {hasAccounts ? (
        <div className="rounded-2xl border border-border/60 bg-card/60 p-4 space-y-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Your accounts today</p>
          <div className="grid grid-cols-2 gap-2">
            <BalanceTile label="Checking" value={checking?.balance ?? 0} />
            <BalanceTile label="Savings" value={savings?.balance ?? 0} />
          </div>
          <p className="text-[11px] text-muted-foreground pt-1">
            {totalScenarios} scenarios this run. Consequences are simulated but recorded as
            transparent ledger entries on your real HeirWise accounts.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-amber-400/40 bg-amber-400/5 p-4 text-sm text-foreground/90">
          You don't have HeirWise accounts yet. Continue to set one up.
        </div>
      )}

      <Button onClick={onStart} className="w-full gap-2">
        Begin Run <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

const OnboardAccounts = ({ onSetup }: { onSetup: () => void }) => (
  <div className="space-y-5">
    <div className="flex flex-col items-center text-center space-y-3">
      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20">
        <DollarSign className="w-8 h-8 text-primary" />
      </div>
      <h2 className="font-heading text-lg font-bold text-foreground">Set up your HeirWise financial profile first</h2>
      <p className="text-sm text-muted-foreground max-w-md">
        Scam Defense uses your HeirWise accounts so the choices feel real. Create a checking or
        savings account before starting this simulator.
      </p>
    </div>
    <Button onClick={onSetup} className="w-full">Set Up Financial Profile</Button>
  </div>
);

const BalanceTile = ({ label, value, deltaBaseline }: { label: string; value: number; deltaBaseline?: number }) => {
  const dropped = deltaBaseline !== undefined && value < deltaBaseline;
  return (
    <div className={`rounded-xl border p-3 transition-colors ${dropped ? "border-destructive/40 bg-destructive/5" : "border-border/60 bg-muted/30"}`}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`font-heading text-base font-bold ${dropped ? "text-destructive" : "text-foreground"}`}>{money(value)}</p>
      {deltaBaseline !== undefined && value < deltaBaseline && (
        <p className="text-[10px] text-destructive mt-0.5">−{money(deltaBaseline - value)}</p>
      )}
    </div>
  );
};

/* ----------------- Simulator (scenario + consequence) ----------------- */

const SimulatorView = ({
  scenario, index, total, forecast, resources, foundHotspots,
  falsePositives, submitted,
  onHotspot, onFlagWord, onSubmitEvidence,
  onAction, decisions, pendingDecision, onAdvance, stage,
}: {
  scenario: ScamScenario;
  index: number; total: number;
  forecast: Record<AccountTarget, number>;
  resources: Resources;
  foundHotspots: Set<string>;
  falsePositives: Set<string>;
  submitted: boolean;
  onHotspot: (h: Hotspot) => void;
  onFlagWord: (tokenId: string, word: string) => void;
  onSubmitEvidence: () => void;
  onAction: (id: ActionId) => void;
  decisions: DecisionRecord[];
  pendingDecision: DecisionRecord | null;
  onAdvance: () => void;
  stage: Stage;
}) => {
  const totalLost = decisions.reduce((s, d) => s + (d.moneyDelta < 0 ? -d.moneyDelta : 0), 0);
  const Icon = TYPE_ICON[scenario.type] ?? Mail;
  const SourceIcon = SOURCE_ICON[scenario.source] ?? Inbox;
  const inConsequence = stage === "consequence" && pendingDecision;
  const riskTier = resources.identityRisk >= 60 ? "High" : resources.identityRisk >= 25 ? "Elevated" : "Low";

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <BalanceTile label="Checking" value={forecast.checking} />
        <BalanceTile label="Savings" value={forecast.savings} />
        <MeterTile icon={Fingerprint} label="Identity risk" value={`${riskTier}`} accent={riskTier === "High" ? "danger" : riskTier === "Elevated" ? "warn" : "ok"} />
        <MeterTile icon={Smile} label="Composure" value={`${resources.composure}/100`} accent={resources.composure < 40 ? "danger" : resources.composure < 70 ? "warn" : "ok"} />
      </div>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>Run progress: {index + 1} of {total}</span>
        <span>Scam losses this run: <span className={totalLost > 0 ? "text-destructive font-medium" : "text-foreground"}>{money(totalLost)}</span></span>
      </div>

      {/* Source chip */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <SourceIcon className="w-3.5 h-3.5" />
        <span>{scenario.source}</span>
        <ChevronRight className="w-3 h-3" />
        <Icon className="w-3.5 h-3.5" />
        <span className="capitalize">{scenario.type.replace("_", " ")}</span>
      </div>

      {/* Task instruction */}
      {!submitted && !inConsequence && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
          <p className="text-xs text-foreground/90 leading-relaxed">
            <span className="font-medium text-primary">Your task: </span>
            Review the message and tap every part that seems suspicious before making your decision.
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Some scams use urgency, fake domains, or requests for private information. Find the warning signs yourself.
          </p>
        </div>
      )}

      {/* Scene */}
      <SceneRenderer
        scenario={scenario}
        foundHotspots={foundHotspots}
        falsePositives={falsePositives}
        submitted={submitted}
        onHotspot={onHotspot}
        onFlagWord={onFlagWord}
      />

      {/* Evidence panel */}
      <EvidencePanel
        scenario={scenario}
        foundHotspots={foundHotspots}
        falsePositives={falsePositives}
        submitted={submitted}
      />

      {/* Submit evidence gate */}
      {!submitted && !inConsequence && (
        <Button onClick={onSubmitEvidence} className="w-full gap-2">
          <Search className="w-4 h-4" />
          Submit evidence ({foundHotspots.size + falsePositives.size} flagged)
        </Button>
      )}

      {/* Action bar OR consequence */}
      {submitted && !inConsequence && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Choose your action</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {scenario.actions.map(a => (
              <button
                key={a.id}
                onClick={() => onAction(a.id)}
                // Neutral, identical styling for every option — tone is intentionally
                // ignored here so the correct action is not given away by color before
                // the learner commits. Tone-based feedback shows in ConsequenceCard
                // after submission.
                className="group relative overflow-hidden rounded-xl border border-cyan-400/20 bg-gradient-to-br from-card/60 via-card/40 to-card/30 p-3 text-left text-sm backdrop-blur-md transition-all hover:border-cyan-300/45 hover:bg-card/70 hover:shadow-[0_0_18px_-8px_hsl(192_90%_60%/0.55)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40"
              >
                <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/40 to-transparent" />
                <span className="font-medium text-foreground">{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {inConsequence && pendingDecision && (
        <ConsequenceCard d={pendingDecision} scenario={scenario} onAdvance={onAdvance} last={index + 1 === total} />
      )}
    </div>
  );
};

const MeterTile = ({ icon: Icon, label, value, accent }: { icon: React.ElementType; label: string; value: string; accent: "ok" | "warn" | "danger" }) => (
  <div className={`rounded-xl border p-3 ${accent === "danger" ? "border-destructive/40 bg-destructive/5" : accent === "warn" ? "border-amber-400/40 bg-amber-400/5" : "border-border/60 bg-muted/30"}`}>
    <div className="flex items-center gap-1.5 text-muted-foreground"><Icon className="w-3.5 h-3.5" /><span className="text-[10px] uppercase tracking-wide">{label}</span></div>
    <p className={`font-heading text-sm font-bold mt-0.5 ${accent === "danger" ? "text-destructive" : accent === "warn" ? "text-amber-400" : "text-foreground"}`}>{value}</p>
  </div>
);

/** Renders the scene and wraps hotspot phrases in clickable spans. */
const SceneRenderer = ({
  scenario, foundHotspots, falsePositives, submitted, onHotspot, onFlagWord,
}: {
  scenario: ScamScenario;
  foundHotspots: Set<string>;
  falsePositives: Set<string>;
  submitted: boolean;
  onHotspot: (h: Hotspot) => void;
  onFlagWord: (tokenId: string, word: string) => void;
}) => {
  const s = scenario.scene;
  const renderRegion = (region: Hotspot["region"], text?: string, className = "") => {
    if (!text) return null;
    const spots = scenario.hotspots.filter(h => h.region === region);
    return (
      <span className={className}>{interleave(text, spots, region, foundHotspots, falsePositives, submitted, onHotspot, onFlagWord)}</span>
    );
  };

  return (
    <div className="rounded-2xl border border-border bg-card/70 overflow-hidden">
      {/* Header strip varies by type */}
      {s.displayedUrl && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border/60 bg-muted/50 text-[11px] font-mono text-muted-foreground">
          <Globe className="w-3 h-3" />
          {renderRegion("url", s.displayedUrl, "text-foreground/90")}
        </div>
      )}
      <div className="p-4 space-y-2">
        {s.from && (
          <p className="text-xs text-muted-foreground">
            From: <span className="text-foreground">{renderRegion("sender", s.from)}</span>
            {s.timestamp && <span className="ml-2 text-[10px]">· {s.timestamp}</span>}
          </p>
        )}
        {s.subject && (
          <p className="font-heading font-semibold text-sm text-foreground">
            {renderRegion("subject", s.subject)}
          </p>
        )}
        <p className="text-sm text-foreground/90 leading-relaxed">
          {renderRegion("body", s.body)}
        </p>
        {(s.attachment || s.productName || s.rating) && (
          <div className="pt-1 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
            {s.attachment && <span className="px-2 py-0.5 rounded-md border border-border/60 bg-muted/40">📎 {renderRegion("meta", s.attachment)}</span>}
            {s.productName && <span className="px-2 py-0.5 rounded-md border border-border/60 bg-muted/40">{s.productName} · {money(s.amount ?? 0)}</span>}
            {s.rating && <span className="px-2 py-0.5 rounded-md border border-border/60 bg-muted/40">{renderRegion("meta", s.rating)}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

/** Split text into clickable word tokens. Hotspot phrases match as one token;
 * everything else is split into individual words that can be flagged as suspicious
 * (false positives). No visible affordance pre-click — learners must investigate. */
function interleave(
  text: string,
  spots: Hotspot[],
  region: Hotspot["region"],
  found: Set<string>,
  falsePositives: Set<string>,
  submitted: boolean,
  onClick: (h: Hotspot) => void,
  onFlagWord: (tokenId: string, word: string) => void,
): React.ReactNode[] {
  const sorted = [...spots].sort((a, b) => b.phrase.length - a.phrase.length);
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let wordIndex = 0;

  const renderWords = (slice: string) => {
    if (!slice) return;
    // Split on word boundaries while preserving whitespace/punctuation.
    const parts = slice.split(/(\s+|[.,;:!?()[\]"'—–-])/);
    for (const part of parts) {
      if (!part) continue;
      const isWord = /\w/.test(part);
      if (!isWord) {
        nodes.push(<span key={`t-${region}-${wordIndex++}`}>{part}</span>);
        continue;
      }
      const tokenId = `${region}:w:${wordIndex++}`;
      const flagged = falsePositives.has(tokenId);
      let cls = "inline transition-colors";
      let title = "Tap if this looks suspicious";
      if (submitted) {
        if (flagged) {
          cls += " bg-destructive/15 text-destructive line-through decoration-destructive/60 rounded px-0.5";
          title = "You flagged this, but it isn't a scam signal";
        }
      } else if (flagged) {
        cls += " bg-amber-400/25 text-amber-200 rounded px-0.5";
        title = "Flagged — tap to unflag";
      } else {
        cls += " hover:bg-primary/10 hover:text-primary cursor-pointer rounded px-0.5";
      }
      nodes.push(
        <button
          key={`w-${region}-${wordIndex}`}
          type="button"
          disabled={submitted}
          onClick={(e) => { e.stopPropagation(); onFlagWord(tokenId, part); }}
          className={cls}
          title={title}
        >
          {part}
        </button>
      );
    }
  };

  while (cursor < text.length) {
    const remaining = text.slice(cursor);
    let bestIdx = -1; let bestSpot: Hotspot | null = null;
    for (const sp of sorted) {
      const i = remaining.indexOf(sp.phrase);
      if (i !== -1 && (bestIdx === -1 || i < bestIdx)) { bestIdx = i; bestSpot = sp; }
    }
    if (!bestSpot || bestIdx === -1) {
      renderWords(remaining);
      break;
    }
    if (bestIdx > 0) renderWords(remaining.slice(0, bestIdx));
    const isFound = found.has(bestSpot.id);
    const spot = bestSpot;
    // Pre-submit: no visible affordance for hotspots (looks like normal text).
    // After submit: green tint if found, red outline if missed.
    let cls = "inline rounded px-0.5 transition-colors";
    let title = "";
    if (submitted) {
      if (isFound) {
        cls += " bg-emerald-400/20 text-emerald-200 border border-emerald-400/40";
        title = `Correct: ${spot.label}`;
      } else {
        cls += " bg-destructive/15 text-destructive border border-destructive/40";
        title = `Missed: ${spot.label}`;
      }
    } else if (isFound) {
      cls += " bg-amber-400/25 text-amber-200";
      title = spot.label;
    } else {
      cls += " cursor-pointer hover:bg-primary/10 hover:text-primary";
      title = "Tap if this looks suspicious";
    }
    nodes.push(
      <button
        key={`hs-${region}-${wordIndex++}`}
        type="button"
        disabled={submitted}
        onClick={(e) => { e.stopPropagation(); onClick(spot); }}
        className={cls}
        title={title}
      >
        {spot.phrase}
      </button>
    );
    cursor += bestIdx + bestSpot.phrase.length;
  }
  return nodes;
}

const EvidencePanel = ({
  scenario, foundHotspots, falsePositives, submitted,
}: {
  scenario: ScamScenario;
  foundHotspots: Set<string>;
  falsePositives: Set<string>;
  submitted: boolean;
}) => {
  const fpCount = falsePositives.size;
  const missed = scenario.hotspots.filter(h => !foundHotspots.has(h.id));
  const risk = !submitted
    ? { label: "Investigating", color: "text-muted-foreground", bg: "bg-muted/30" }
    : foundHotspots.size === scenario.hotspots.length && fpCount === 0
      ? { label: "Clean detection", color: "text-emerald-300", bg: "bg-emerald-400/10" }
      : foundHotspots.size >= Math.ceil(scenario.hotspots.length / 2)
        ? { label: "Partial detection", color: "text-amber-300", bg: "bg-amber-400/10" }
        : { label: "Missed key signals", color: "text-destructive", bg: "bg-destructive/10" };
  return (
    <div className="rounded-xl border border-border/60 bg-card/50 p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
          <Search className="w-3 h-3" /> Evidence collected ({foundHotspots.size}/{scenario.hotspots.length})
          {fpCount > 0 && <span className="text-destructive ml-1">· {fpCount} false flag{fpCount === 1 ? "" : "s"}</span>}
        </p>
        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${risk.bg} ${risk.color}`}>{risk.label}</span>
      </div>
      {!submitted && foundHotspots.size === 0 && fpCount === 0 ? (
        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
          <Eye className="w-3 h-3" /> Tap any part of the message that seems suspicious, then submit your evidence.
        </p>
      ) : !submitted ? (
        <p className="text-[11px] text-muted-foreground">Submit when you've flagged everything that looks off — you'll see what's correct after.</p>
      ) : (
        <div className="space-y-2">
          {scenario.hotspots.filter(h => foundHotspots.has(h.id)).length > 0 && (
            <ul className="space-y-1">
              {scenario.hotspots.filter(h => foundHotspots.has(h.id)).map(h => (
                <li key={h.id} className="text-[12px] text-foreground/90 flex gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
                  <span><span className="font-medium">{h.label}.</span> <span className="text-muted-foreground">{h.explanation}</span></span>
                </li>
              ))}
            </ul>
          )}
          {missed.length > 0 && (
            <ul className="space-y-1 pt-1 border-t border-border/40">
              {missed.map(h => (
                <li key={h.id} className="text-[12px] text-foreground/80 flex gap-1.5">
                  <AlertTriangle className="w-3 h-3 text-destructive mt-0.5 shrink-0" />
                  <span><span className="font-medium text-destructive">Missed — {h.label}.</span> <span className="text-muted-foreground">{h.explanation}</span></span>
                </li>
              ))}
            </ul>
          )}
          {fpCount > 0 && (
            <p className="text-[11px] text-muted-foreground pt-1 border-t border-border/40 flex items-center gap-1">
              <XCircle className="w-3 h-3 text-destructive" />
              You flagged {fpCount} item{fpCount === 1 ? "" : "s"} that weren't actually scam signals.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const ConsequenceCard = ({ d, scenario, onAdvance, last }: { d: DecisionRecord; scenario: ScamScenario; onAdvance: () => void; last: boolean }) => {
  const tone = d.isBest ? "safe" : d.moneyDelta < 0 || d.identityRiskDelta > 10 ? "danger" : "neutral";
  return (
    <div className="space-y-3">
      <div className={`rounded-2xl border p-4 ${
        tone === "safe" ? "border-emerald-400/40 bg-emerald-400/5" :
        tone === "danger" ? "border-destructive/40 bg-destructive/5" :
        "border-border/60 bg-card/60"
      }`}>
        <div className="flex items-center gap-2 mb-2">
          {tone === "safe" ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> :
           tone === "danger" ? <AlertTriangle className="w-5 h-5 text-destructive" /> :
           <ShieldCheck className="w-5 h-5 text-primary" />}
          <p className="font-heading font-semibold text-sm text-foreground">
            {d.isBest ? "Strong defense" : d.moneyDelta < 0 ? "You took a hit" : "Outcome"}
          </p>
        </div>
        <p className="text-sm text-foreground/90 leading-relaxed">{d.message}</p>
        <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
          {d.moneyDelta < 0 && <span className="px-2 py-0.5 rounded-md bg-destructive/15 text-destructive">{money(d.moneyDelta)} {d.account}</span>}
          {d.identityRiskDelta > 0 && <span className="px-2 py-0.5 rounded-md bg-amber-400/15 text-amber-300">+{d.identityRiskDelta} identity risk</span>}
          {d.composureDelta !== 0 && <span className={`px-2 py-0.5 rounded-md ${d.composureDelta > 0 ? "bg-emerald-400/15 text-emerald-300" : "bg-muted/40 text-muted-foreground"}`}>{d.composureDelta > 0 ? "+" : ""}{d.composureDelta} composure</span>}
          {d.triggersRecovery && <span className="px-2 py-0.5 rounded-md bg-destructive/15 text-destructive">Recovery checklist triggered</span>}
        </div>
        {!d.isBest && (
          <p className="text-[11px] text-muted-foreground mt-3">
            Better path: <span className="text-foreground">{scenario.actions.find(a => a.id === scenario.bestAction)?.label}</span>.
            <span className="block mt-1">{scenario.explainer}</span>
          </p>
        )}
      </div>
      <Button onClick={onAdvance} className="w-full gap-2">
        {last ? "See your run summary" : "Next scenario"} <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

/* ------------------------------ Outcome ------------------------------ */

const OutcomeScreen = ({
  decisions, resources, forecast, conceptKeys, committing, accounts, onReplay, onExit, exitLabel, onRetakeModule,
}: {
  decisions: DecisionRecord[];
  resources: Resources;
  forecast: Record<AccountTarget, number>;
  conceptKeys: string[];
  committing: boolean;
  accounts: Account[];
  onReplay: () => void;
  onExit: () => void;
  exitLabel?: string;
  onRetakeModule?: () => void;
}) => {
  const moneyLost = decisions.reduce((s, d) => s + (d.moneyDelta < 0 ? -d.moneyDelta : 0), 0);
  const protectedCount = decisions.filter(d => d.isBest).length;
  const moneyAtRiskAvoided = decisions
    .filter(d => d.isBest)
    .reduce((s, d) => {
      const sc = SCAM_SCENARIOS.find(x => x.id === d.scenarioId);
      const worst = sc ? Object.values(sc.consequences).reduce((m, c) => Math.min(m, c?.moneyDelta ?? 0), 0) : 0;
      return s + Math.abs(worst);
    }, 0);
  const hotspotsFound = decisions.reduce((s, d) => s + d.hotspotsFound, 0);
  const hotspotsTotal = decisions.reduce((s, d) => s + d.hotspotsTotal, 0);
  const totalFalsePositives = decisions.reduce((s, d) => s + (d.falsePositives ?? 0), 0);
  const dangerous = decisions.filter(d => d.moneyDelta < 0 || d.identityRiskDelta > 10).length;

  const rawScore =
    (hotspotsTotal > 0 ? (hotspotsFound / hotspotsTotal) * 50 : 0) +
    (decisions.length > 0 ? (protectedCount / decisions.length) * 40 : 0) +
    (resources.identityRisk < 25 ? 10 : 0);
  const fpPenalty = Math.min(totalFalsePositives * 2, 15);
  const score = Math.max(0, Math.round(rawScore - fpPenalty));

  const tier =
    score >= 80 ? { title: "Strong Digital Defense", body: "You consistently identified evidence and chose safe actions." } :
    score >= 55 ? { title: "Building the habit", body: "You spotted several patterns. A few slipped past — replay to lock them in." } :
    { title: "Compromised", body: "Scammers exploited a few gaps. The same patterns repeat — practice the verification habit and try again." };

  const best = decisions.find(d => d.isBest);
  const worst = [...decisions].sort((a, b) => (a.moneyDelta - b.moneyDelta) || (b.identityRiskDelta - a.identityRiskDelta))[0];
  const conceptChips = conceptKeys.length > 0
    ? conceptKeys.map(k => FROZEN_DICTIONARY_CONCEPTS[k]).filter(Boolean)
    : ["Independent verification"];

  const parentSummary = buildParentSummary({ decisions, moneyLost, identityRisk: resources.identityRisk });

  return (
    <div className="space-y-5">
      {committing && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Committing simulator outcomes to your HeirWise ledger…</div>
      )}

      <div className="flex flex-col items-center text-center space-y-3">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20">
          <ShieldCheck className="w-8 h-8 text-primary" />
        </div>
        <h2 className="font-heading text-xl font-bold text-foreground">{tier.title}</h2>
        <p className="text-sm text-muted-foreground max-w-md">{tier.body}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="Score" value={`${score}%`} />
        <StatTile label="Money lost" value={money(moneyLost)} accent={moneyLost > 0 ? "danger" : "ok"} />
        <StatTile label="Money protected" value={money(moneyAtRiskAvoided)} accent="ok" />
        <StatTile label="Identity risk" value={resources.identityRisk >= 60 ? "High" : resources.identityRisk >= 25 ? "Elevated" : "Low"} accent={resources.identityRisk >= 60 ? "danger" : resources.identityRisk >= 25 ? "warn" : "ok"} />
      </div>

      <div className="rounded-2xl border border-border/60 bg-card/60 p-4 space-y-3">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Final HeirWise balances</p>
        <div className="grid grid-cols-2 gap-2">
          <BalanceTile label="Checking" value={accounts.find(a => a.account_type === "checking")?.balance ?? forecast.checking} />
          <BalanceTile label="Savings" value={accounts.find(a => a.account_type === "savings")?.balance ?? forecast.savings} />
        </div>
        <div className="grid grid-cols-3 gap-2 text-[11px] text-muted-foreground pt-1">
          <span>Evidence: <span className="text-foreground">{hotspotsFound}/{hotspotsTotal}</span></span>
          <span>Scams defeated: <span className="text-foreground">{protectedCount}/{decisions.length}</span></span>
          <span>False flags: <span className={totalFalsePositives > 0 ? "text-destructive" : "text-foreground"}>{totalFalsePositives}</span></span>
        </div>
      </div>

      {(best || worst) && (
        <div className="grid gap-2 sm:grid-cols-2">
          {best && <DecisionHighlight label="Best decision" d={best} tone="safe" />}
          {worst && !worst.isBest && <DecisionHighlight label="Needs practice" d={worst} tone="danger" />}
        </div>
      )}

      <div className="rounded-2xl border border-border/60 bg-card/60 p-4">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Concepts practiced</p>
        <div className="flex flex-wrap gap-1.5">
          {conceptChips.map(c => (
            <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">{c}</span>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card/60 p-4">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Parent summary</p>
        <p className="text-sm text-foreground/90 leading-relaxed">{parentSummary}</p>
      </div>

      <div className="grid gap-2">
        <Button onClick={onReplay} variant="outline" className="w-full gap-2">
          <RotateCcw className="w-4 h-4" /> Replay with a new mix
        </Button>
        <Button onClick={onExit} className="w-full">{exitLabel ?? "Back to Dashboard"}</Button>
        {onRetakeModule && (
          <Button
            variant="ghost"
            onClick={onRetakeModule}
            className="w-full text-muted-foreground hover:text-destructive"
          >
            Retake Module
          </Button>
        )}
      </div>
    </div>
  );
};

const StatTile = ({ label, value, accent = "ok" }: { label: string; value: string; accent?: "ok" | "warn" | "danger" }) => (
  <div className={`rounded-xl border p-3 ${accent === "danger" ? "border-destructive/40 bg-destructive/5" : accent === "warn" ? "border-amber-400/40 bg-amber-400/5" : "border-border/60 bg-muted/30"}`}>
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`font-heading text-base font-bold mt-0.5 ${accent === "danger" ? "text-destructive" : accent === "warn" ? "text-amber-400" : "text-foreground"}`}>{value}</p>
  </div>
);

const DecisionHighlight = ({ label, d, tone }: { label: string; d: DecisionRecord; tone: "safe" | "danger" }) => {
  const sc = SCAM_SCENARIOS.find(s => s.id === d.scenarioId);
  return (
    <div className={`rounded-xl border p-3 ${tone === "safe" ? "border-emerald-400/30 bg-emerald-400/5" : "border-destructive/30 bg-destructive/5"}`}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground mt-0.5">{sc?.title ?? d.scenarioId}</p>
      <p className="text-[12px] text-muted-foreground mt-1">{d.message}</p>
    </div>
  );
};

/** Build a parent-facing summary grounded in actual decisions. No invented data. */
function buildParentSummary({ decisions, moneyLost, identityRisk }: { decisions: DecisionRecord[]; moneyLost: number; identityRisk: number }): string {
  if (decisions.length === 0) return "Your learner started but did not complete a Scam Defense run.";
  const wins = decisions.filter(d => d.isBest).length;
  const losses = decisions.length - wins;
  const accountsHit = Array.from(new Set(decisions.filter(d => d.moneyDelta < 0 && d.account).map(d => d.account!)));
  const parts: string[] = [];
  if (wins > 0) parts.push(`Your learner identified scam patterns and chose safe actions on ${wins} of ${decisions.length} scenarios.`);
  if (moneyLost > 0) {
    parts.push(`A simulated loss of ${money(moneyLost)} was recorded${accountsHit.length ? ` against the ${accountsHit.join(" and ")} account${accountsHit.length > 1 ? "s" : ""}` : ""}.`);
  } else {
    parts.push("No simulated money was lost this run.");
  }
  if (identityRisk >= 60) parts.push("Identity risk reached high — recommended practice: URL checking, OTP safety, and credential hygiene.");
  else if (identityRisk >= 25) parts.push("Identity risk increased slightly — practice independent verification before clicking links or sharing details.");
  if (losses > 0 && wins === 0) parts.push("Recommended follow-up: independent verification and pausing before urgent asks.");
  return parts.join(" ");
}

export default ScamDefenseModule;
